import { useState, useEffect, useRef } from "react";
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer
} from "recharts";
import {
  ChevronDown, ChevronUp, ExternalLink, Download, FileText,
  X, Copy, Check
} from "lucide-react";
import { useInView } from "./hooks";

export function AnimatedCounter({ end, duration = 2000, prefix = "", suffix = "", active }) {
  const [count, setCount] = useState(0);
  const started = useRef(false);
  useEffect(() => {
    if (!active || started.current) return;
    started.current = true;
    const steps = 60;
    const inc = end / steps;
    let c = 0;
    const t = setInterval(() => {
      c += inc;
      if (c >= end) { setCount(end); clearInterval(t); }
      else setCount(Math.floor(c));
    }, duration / steps);
    return () => clearInterval(t);
  }, [active, end, duration]);
  const d = count >= 1000000 ? `${(count / 1000000).toFixed(0)}M`
    : count >= 1000 ? `${(count / 1000).toFixed(0)}K` : count.toString();
  return <span>{prefix}{d}{suffix}</span>;
}

export function Badge({ children, color = "bg-blue-700" }) {
  return <span className={`${color} text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider inline-block`}>{children}</span>;
}

export function SW({ children, id, dark = false, className = "" }) {
  const ref = useRef(null);
  const vis = useInView(ref);
  return (
    <div id={id} ref={ref} data-testid={`section-${id}`}
      className={`min-h-screen flex flex-col justify-center px-6 md:px-16 lg:px-24 py-20 ${dark ? "section-dark text-white" : "section-light text-slate-900"} section-animate ${vis ? "visible" : ""} ${className}`}>
      <div className="max-w-6xl mx-auto w-full">{children}</div>
    </div>
  );
}

export function RadarMini({ data, size = 130 }) {
  const cd = [
    { subject: "Data", A: data.da, fullMark: 5 },
    { subject: "Method", A: data.mf, fullMark: 5 },
    { subject: "Partners", A: data.pr, fullMark: 5 },
    { subject: "Timeline", A: data.tl, fullMark: 5 },
  ];
  return (
    <ResponsiveContainer width={size} height={size}>
      <RadarChart data={cd} cx="50%" cy="50%">
        <PolarGrid stroke="#334155" />
        <PolarAngleAxis dataKey="subject" tick={{ fill: "#94a3b8", fontSize: 9 }} />
        <PolarRadiusAxis angle={90} domain={[0, 5]} tick={false} axisLine={false} />
        <Radar dataKey="A" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.25} strokeWidth={2} />
      </RadarChart>
    </ResponsiveContainer>
  );
}

export function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
      className="ml-2 text-slate-400 hover:text-white transition" title="Copy">
      {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}

export function KobzarQuote({ quote, dark = false }) {
  return (
    <div className="my-8 text-center" data-testid="kobzar-quote">
      <div className={`inline-block max-w-2xl ${dark ? "border-slate-700/40" : "border-slate-300/60"} border-l-2 border-r-2 px-8 py-4`}>
        <p className={`text-sm italic leading-relaxed ${dark ? "text-slate-400/90" : "text-slate-500/90"}`} style={{ fontFamily: 'var(--font-body)' }}>
          &ldquo;{quote.text}&rdquo;
        </p>
        <p className={`text-[11px] mt-2 ${dark ? "text-slate-600" : "text-slate-400"}`} style={{ fontFamily: 'var(--font-body)' }}>
          — Taras Shevchenko, <em>Kobzar</em> &middot; {quote.poem}
        </p>
      </div>
    </div>
  );
}

export function CitationLink({ href, children }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" data-testid="citation-link"
      className="inline-flex items-center gap-1 text-[11px] text-blue-500 hover:text-blue-400 transition underline decoration-blue-500/30 hover:decoration-blue-400/60" style={{ fontFamily: 'var(--font-body)' }}>
      {children}<ExternalLink className="w-2.5 h-2.5 flex-shrink-0" />
    </a>
  );
}

export function CitationBar({ citations, dark = false }) {
  const [open, setOpen] = useState(false);
  if (!citations || citations.length === 0) return null;
  return (
    <div className="mt-6" data-testid="citation-bar">
      <button onClick={() => setOpen(!open)}
        className={`text-[11px] font-medium flex items-center gap-1.5 transition ${dark ? "text-slate-500 hover:text-slate-400" : "text-slate-400 hover:text-slate-600"}`} style={{ fontFamily: 'var(--font-body)' }}>
        <FileText className="w-3 h-3" />
        {open ? "Hide" : "Show"} Sources ({citations.length})
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
      {open && (
        <div className={`mt-2 rounded-xl p-3 space-y-1.5 tooltip-enter ${dark ? "bg-slate-800/60 border border-slate-700/40" : "bg-slate-50 border border-slate-200"}`}>
          {citations.map((c, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className={`text-[10px] font-bold mt-0.5 flex-shrink-0 ${dark ? "text-slate-600" : "text-slate-400"}`}>[{i + 1}]</span>
              <a href={c.url} target="_blank" rel="noopener noreferrer"
                className={`text-[11px] leading-snug transition ${dark ? "text-slate-400 hover:text-sky-400" : "text-slate-500 hover:text-blue-600"}`} style={{ fontFamily: 'var(--font-body)' }}>
                {c.label} <ExternalLink className="w-2.5 h-2.5 inline ml-0.5" />
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function PdfPreviewModal({ url, name, onClose }) {
  if (!url) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="pdf-preview-modal" onClick={onClose}>
      <div className="relative w-[90vw] h-[85vh] max-w-5xl bg-white rounded-2xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 bg-slate-900 border-b border-slate-700">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-sky-400" />
            <span className="text-sm font-semibold text-white" style={{ fontFamily: 'var(--font-display)' }}>{name}</span>
          </div>
          <div className="flex items-center gap-3">
            <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs text-sky-400 hover:text-sky-300 flex items-center gap-1 transition" style={{ fontFamily: 'var(--font-body)' }}>
              <Download className="w-3.5 h-3.5" /> Download
            </a>
            <button onClick={onClose} className="text-slate-400 hover:text-white transition"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <iframe src={url} title={name} className="w-full" style={{ height: 'calc(100% - 48px)' }} />
      </div>
    </div>
  );
}
